import type { Logger } from "@expert/logging";
import type { GaiaPayloadConfigs, MessageType } from "@expert/shared-types";

import { type TimelineFeatures, useFeatures } from "@expert/features";
import { useEffect, useState } from "react";

import type { MessageTypeConfigRuntime } from "../../../config/types";
import type { ExpertAssistMessage } from "../../../shared-types";

import { getMessageTypeConfig } from "../../../config";
import { DEAD_AIR_TAG } from "../../../shared-types";
import { useTimelineStore } from "../../../state";
import { useQuickActionsManager } from "../../quick-actions";
import { useMessageAutoscroll } from "./useMessageAutoscroll";
import { useMessageOverflow } from "./useMessageOverflow";
import { useUnreadMessages } from "./useUnreadMessages";

interface UseTimelineItemProps {
    type: MessageType;
    message?: ExpertAssistMessage;
    isLoading: boolean;
    isLastBotMessage: boolean;
    isLastProactiveMessage: boolean;
    scrollRef: React.RefObject<HTMLDivElement | null> | undefined;
    logger: Logger;
    gaiaPayloadProps: GaiaPayloadConfigs;
}

export function useTimelineItem({
    type,
    message,
    isLoading,
    isLastBotMessage,
    isLastProactiveMessage,
    scrollRef,
    logger,
    gaiaPayloadProps,
}: UseTimelineItemProps) {
    const { getTimelineState, setIsMessageUnread, currentSessionId } = useTimelineStore();
    const { callSid } = getTimelineState(currentSessionId);
    const {
        features: { isProactiveEnabled },
    } = useFeatures<TimelineFeatures>();

    const { overflowRef, isOverflowing, isExpanded, setIsExpanded } = useMessageOverflow({ initialExpanded: false });

    const { messageIntersectionRef } = useUnreadMessages({
        scrollRef,
        message,
        currentSessionId,
        setIsMessageUnread,
    });

    const config = computeRuntimeConfig({
        type,
        isLastProactiveMessage,
        isExpanded,
        isOverflowing,
        message,
        isLoading,
        isLastBotMessage,
        callSid,
        isProactiveEnabled,
    });

    const actions = useQuickActionsManager({
        currentMessageType: type,
        messageId: message?.id,
        tag: message?.tag,
        isOverflowing,
        isExpanded: config.shouldShowExpanded,
        isLastBotMessage,
        isLastProactiveMessage,
        setIsExpanded,
        aiQuickActions: message?.metadata?.aiQuickActions,
    });

    const [isQuickActionsVisible, setIsQuickActionsVisible] = useState<boolean>(actions.length > 0);

    useEffect(() => {
        if (actions.length === 0) {
            setIsQuickActionsVisible(false);
        }
    }, [actions]);

    useMessageAutoscroll({
        scrollRef,
        shouldShowExpanded: config.shouldShowExpanded,
        isOverflowing,
        isLastBotMessage,
        isUnread: message?.isUnread,
    });

    return {
        config,
        overflowRef,
        messageIntersectionRef,
        quickActionGroupProps: {
            actions,
            gaiaPayloadProps,
            scrollRef,
            setIsQuickActionsVisible,
            logger,
        },
        isQuickActionsVisible,
    };
}

function computeRuntimeConfig({
    type,
    isLastProactiveMessage,
    isExpanded,
    isOverflowing,
    message,
    isLoading,
    isLastBotMessage,
    callSid,
    isProactiveEnabled,
}: {
    type: MessageType;
    isLastProactiveMessage: boolean;
    isExpanded: boolean;
    isOverflowing: boolean;
    message?: ExpertAssistMessage;
    isLoading: boolean;
    isLastBotMessage: boolean;
    callSid?: string;
    isProactiveEnabled: boolean;
}): MessageTypeConfigRuntime {
    let config = getMessageTypeConfig(type);
    const { tag } = message ?? {};

    const isProactiveMessage = isProactiveEnabled && config.supportsProactiveMode;
    const shouldShowProactive = isProactiveMessage && isLastProactiveMessage;
    const isBotListening = isProactiveEnabled && !isLoading && isLastBotMessage && !!callSid;

    // Handle bubble style overrides based on runtime context
    if ((type === "Default" || type === "Generic") && !isLastProactiveMessage) {
        config = { ...config, bubbleStyle: "reactive" };
    }
    if (type === "Proactive" && !shouldShowProactive) {
        config = { ...config, bubbleStyle: "reactive" };
    }
    if (type === "Proactive" && tag === DEAD_AIR_TAG) {
        config = { ...config, bubbleStyle: "deadAirAssist" };
    }

    // Variant controls link colors and is independent of bubble style.
    // Default/Generic always use proactive variant (green links) even with reactive bubble styling.
    let dataVariant: "proactive" | "reactive" = "reactive";
    if (type === "Default" || type === "Generic") {
        dataVariant = "proactive";
    } else if (shouldShowProactive) {
        dataVariant = "proactive";
    }

    let shouldShowExpanded = true;
    if (isLastProactiveMessage && !isExpanded && isOverflowing) {
        shouldShowExpanded = false;
    }

    const shouldShowMessageTag = config.supportsTag && !!tag && isLastProactiveMessage;
    const shouldShowFeedback = config.supportsFeedback && shouldShowExpanded && message?.status !== "deactivated";

    return {
        ...config,
        isBotListening,
        shouldShowExpanded,
        shouldShowMessageTag,
        shouldShowFeedback,
        dataVariant,
    };
}
